import React from 'react';
import Icon from '../../../components/AppIcon';

const ClientProgressMatrix = ({ clients, onClientClick, loading }) => {
  const getMilestoneStatus = (milestones, type) => {
    const milestone = milestones?.find(m => m?.milestone_type === type);
    if (!milestone) return { status: 'not_started', percentage: 0 };
    return {
      status: milestone?.milestone_status,
      percentage: milestone?.completion_percentage || 0
    };
  };

  const getStatusColor = (percentage) => {
    if (percentage === 100) return 'bg-success text-white';
    if (percentage >= 50) return 'bg-warning text-white';
    if (percentage > 0) return 'bg-error text-white';
    return 'bg-muted text-muted-foreground';
  };

  const getRiskBadge = (riskLevel) => {
    const styles = {
      low: 'bg-success/10 text-success border-success/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      high: 'bg-error/10 text-error border-error/20'
    };
    return styles?.[riskLevel] || styles?.medium;
  };

  const milestoneTypes = [
    { key: 'organization_profile', label: 'Org Profile' },
    { key: 'employee_import', label: 'Employees' },
    { key: 'compliance_setup', label: 'Compliance' },
    { key: 'team_access', label: 'Team Access' }
  ];

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Client Progress Matrix</h2>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-success"></div>
            <span className="text-muted-foreground">100%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-warning"></div>
            <span className="text-muted-foreground">50-99%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-error"></div>
            <span className="text-muted-foreground">&lt;50%</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Client</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Overall</th>
              {milestoneTypes?.map(type => (
                <th key={type?.key} className="text-center py-3 px-4 text-sm font-semibold text-foreground">
                  {type?.label}
                </th>
              ))}
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Time</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Risk</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">Action</th>
            </tr>
          </thead>
          <tbody>
            {clients?.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-muted-foreground">
                  No clients found matching the filters
                </td>
              </tr>
            ) : (
              clients?.map(client => {
                const orgProfile = getMilestoneStatus(client?.milestones, 'organization_profile');
                const employeeImport = getMilestoneStatus(client?.milestones, 'employee_import');
                const complianceSetup = getMilestoneStatus(client?.milestones, 'compliance_setup');
                const teamAccess = getMilestoneStatus(client?.milestones, 'team_access');

                return (
                  <tr
                    key={client?.id}
                    className="border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => onClientClick(client)}
                  >
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-foreground">{client?.companyName}</p>
                        <p className="text-xs text-muted-foreground">{client?.contactEmail}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm font-semibold text-foreground">
                          {client?.overallProgress}%
                        </span>
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${client?.overallProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded ${getStatusColor(orgProfile?.percentage)}`}>
                        <span className="text-xs font-semibold">{orgProfile?.percentage}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded ${getStatusColor(employeeImport?.percentage)}`}>
                        <span className="text-xs font-semibold">{employeeImport?.percentage}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded ${getStatusColor(complianceSetup?.percentage)}`}>
                        <span className="text-xs font-semibold">{complianceSetup?.percentage}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded ${getStatusColor(teamAccess?.percentage)}`}>
                        <span className="text-xs font-semibold">{teamAccess?.percentage}%</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className="text-sm text-foreground">{client?.timeInStage}d</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getRiskBadge(client?.riskLevel)}`}>
                        {client?.riskLevel?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          onClientClick(client);
                        }}
                        className="text-primary hover:text-primary/80 transition-colors"
                      >
                        <Icon name="ChevronRight" size={20} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientProgressMatrix;